import { supabase } from "@/integrations/supabase/client";
import { classifyBpm, type HrZoneId } from "@/lib/heartRateZones";

export type CoachFrequency = "rare" | "when_needed" | "frequent";

export interface RunningCoachGuideSettings {
  enabled: boolean;
  frequency: CoachFrequency;
  plan: string;
  source?: "manual" | "arnold";
}

export const normalizeCoachPlanText = (plan: unknown): string => {
  if (typeof plan === "string") return plan;
  if (plan == null) return "";
  return String(plan);
};

export type RunningLaunchSettings = {
  activityType: string;
  audioStats: string;
  audioVoice?: "female" | "male";
  gpsActivity?: "run" | "walk" | "hike";
  startTime?: string;
  coachGuide?: RunningCoachGuideSettings;
};

const defaultLaunchSettings = (): RunningLaunchSettings => ({
  activityType: "outdoor",
  audioStats: "5min",
  audioVoice: "female",
  gpsActivity: "run",
});

export const parseRunningSettingsFromStorage = (raw: string | null): RunningLaunchSettings => {
  if (!raw) return defaultLaunchSettings();
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const cg = parsed.coachGuide as Record<string, unknown> | undefined;
    const frequency = cg?.frequency;
    const coachGuide: RunningCoachGuideSettings | undefined = cg
      ? {
          enabled: !!cg.enabled,
          frequency:
            frequency === "rare" || frequency === "when_needed" || frequency === "frequent"
              ? frequency
              : "when_needed",
          plan: normalizeCoachPlanText(cg.plan),
          source: cg.source === "arnold" ? "arnold" : "manual",
        }
      : undefined;
    const gps = parsed.gpsActivity;
    return {
      activityType: typeof parsed.activityType === "string" ? parsed.activityType : "outdoor",
      audioStats: typeof parsed.audioStats === "string" ? parsed.audioStats : "5min",
      audioVoice: parsed.audioVoice === "male" ? "male" : "female",
      startTime: typeof parsed.startTime === "string" ? parsed.startTime : undefined,
      gpsActivity: gps === "walk" || gps === "hike" ? gps : "run",
      coachGuide,
    };
  } catch {
    return defaultLaunchSettings();
  }
};

export const buildRunningLaunchSettings = (
  input: {
    activityType: string;
    audioStats: string;
    audioVoice?: "female" | "male";
    gpsActivity?: "run" | "walk" | "hike";
    coachGuide: RunningCoachGuideSettings;
    startTime: string;
  },
): RunningLaunchSettings => {
  const plan = normalizeCoachPlanText(input.coachGuide.plan);
  return {
    activityType: input.activityType,
    audioStats: input.audioStats,
    audioVoice: input.audioVoice,
    gpsActivity: input.gpsActivity,
    startTime: input.startTime,
    coachGuide: {
      ...input.coachGuide,
      plan,
      enabled: !!(input.coachGuide.enabled && plan.trim()),
    },
  };
};

export interface ParsedRunPlan {
  raw: string;
  targetDistanceKm?: number;
  targetTimeSeconds?: number;
  targetPaceSecondsPerKm?: number;
  intervalDistanceKm?: number;
}

export type PaceVerdict = "on_target" | "too_fast" | "too_slow" | "unknown";

export interface RunningCoachSnapshot {
  plan: string;
  frequency: CoachFrequency;
  distanceKm: number;
  elapsedSeconds: number;
  currentPaceSecondsPerKm: number;
  averagePaceSecondsPerKm: number;
  lastSplitSeconds?: number;
  paceDeltaSeconds?: number;
  /** Phase-specific target pace (sec/km) from the structured plan. */
  targetPaceSecondsPerKm?: number;
  paceVerdict?: PaceVerdict;
  currentBpm?: number | null;
  targetZone?: HrZoneId | null;
  zones?: Array<{ id: string; label?: string; lowerBpm: number; upperBpm: number }>;
  activePhaseLabel?: string;
  activePhaseSummary?: string;
  nextPhaseLabel?: string;
  phaseKind?: string;
  remainingPhaseSeconds?: number;
}

const parseNumber = (value: string) => Number(value.replace(",", "."));

export function parseRunningPlan(plan: string): ParsedRunPlan {
  const text = plan.toLowerCase();
  const parsed: ParsedRunPlan = { raw: plan };

  const distanceMatch = text.match(/(\d+(?:[.,]\d+)?)\s*k(?:m|ilometer)/);
  if (distanceMatch) parsed.targetDistanceKm = parseNumber(distanceMatch[1]);

  const goalMatch = text.match(
    /(\d+(?:[.,]\d+)?)\s*k(?:m|ilometer)?\s*(?:at|in|for|within|under)\s*(\d+(?:[.,]\d+)?)\s*(?:min(?:utes?)?|m\b)/,
  );
  if (goalMatch) {
    parsed.targetDistanceKm = parseNumber(goalMatch[1]);
    parsed.targetTimeSeconds = Math.round(parseNumber(goalMatch[2]) * 60);
  } else {
    const timeMatch = text.match(/\b(?:in|under|within|for|at)\s*(\d{1,2})\s*(?:min(?:utes?)?|m\b)/);
    if (timeMatch) parsed.targetTimeSeconds = Number(timeMatch[1]) * 60;
  }

  const paceColon = text.match(/(\d{1,2}):(\d{2})\s*(?:pace|\/?\s*km|per\s*k(?:m|ilometer))/);
  if (paceColon) {
    parsed.targetPaceSecondsPerKm = Number(paceColon[1]) * 60 + Number(paceColon[2]);
  }
  if (!parsed.targetPaceSecondsPerKm) {
    const paceMinKm = text.match(/(\d+(?:[.,]\d+)?)\s*min\s*(?:\/|per)\s*k(?:m|ilometer)?/);
    if (paceMinKm) parsed.targetPaceSecondsPerKm = Math.round(parseNumber(paceMinKm[1]) * 60);
  }

  const intervalMatch = text.match(/(\d+(?:[.,]\d+)?)\s*k(?:m|ilometer)?\s*interval/);
  if (intervalMatch) parsed.intervalDistanceKm = parseNumber(intervalMatch[1]);

  if (!parsed.targetPaceSecondsPerKm && parsed.targetDistanceKm && parsed.targetTimeSeconds) {
    parsed.targetPaceSecondsPerKm = parsed.targetTimeSeconds / parsed.targetDistanceKm;
  }

  return parsed;
}

export function getCoachMinIntervalSeconds(frequency: CoachFrequency): number {
  if (frequency === "frequent") return 30;
  if (frequency === "rare") return 360;
  return 90;
}

export function formatPaceSpeech(secPerKm: number): string {
  if (!secPerKm || !Number.isFinite(secPerKm)) return "";
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  if (m <= 0) return `${s} seconds per kilometer`;
  if (s === 0) return `${m} minute${m !== 1 ? "s" : ""} per kilometer`;
  return `${m} minute${m !== 1 ? "s" : ""} and ${s} seconds per kilometer`;
}

function zoneLabel(zones: RunningCoachSnapshot["zones"], id: HrZoneId): string {
  const z = zones?.find((x) => x.id === id);
  return z?.label || id.toUpperCase();
}

/** Compare live pace to the phase target. Negative delta = faster than target. */
export function comparePaceToTarget(
  currentSecPerKm: number,
  targetSecPerKm: number,
  toleranceSec = 8,
): { verdict: PaceVerdict; deltaSec: number; message: string } {
  if (
    !currentSecPerKm ||
    !targetSecPerKm ||
    !Number.isFinite(currentSecPerKm) ||
    !Number.isFinite(targetSecPerKm) ||
    currentSecPerKm <= 0 ||
    targetSecPerKm <= 0
  ) {
    return { verdict: "unknown", deltaSec: 0, message: "" };
  }

  const delta = Math.round(currentSecPerKm - targetSecPerKm);
  const cur = formatPaceSpeech(currentSecPerKm);
  const tgt = formatPaceSpeech(targetSecPerKm);

  if (Math.abs(delta) <= toleranceSec) {
    return {
      verdict: "on_target",
      deltaSec: delta,
      message: `Pace is on target at about ${cur}, near your ${tgt} target for this block. Hold steady.`,
    };
  }
  if (delta < 0) {
    return {
      verdict: "too_fast",
      deltaSec: delta,
      message: `You are running too fast — about ${cur} now versus your ${tgt} target for this block. Ease up and slow down.`,
    };
  }
  return {
    verdict: "too_slow",
    deltaSec: delta,
    message: `You are running too slow — about ${cur} now versus your ${tgt} target for this block. Pick up the pace slightly.`,
  };
}

function buildHrFeedback(snapshot: RunningCoachSnapshot): string {
  if (!snapshot.currentBpm || !snapshot.zones?.length) return "";

  const zones = snapshot.zones.map((z) => ({
    id: z.id as HrZoneId,
    label: z.label || z.id,
    lowerBpm: z.lowerBpm,
    upperBpm: z.upperBpm,
  }));
  const currentZone = classifyBpm(snapshot.currentBpm, zones);
  if (!currentZone) return "";

  const z = zones.find((x) => x.id === currentZone);
  if (!snapshot.targetZone) {
    if (z) {
      return `Heart rate is ${Math.round(snapshot.currentBpm)} beats per minute in ${z.label}.`;
    }
    return "";
  }

  if (currentZone === snapshot.targetZone) {
    const target = zones.find((x) => x.id === snapshot.targetZone);
    if (target) {
      return `Heart rate ${Math.round(snapshot.currentBpm)} is in your target ${zoneLabel(snapshot.zones, snapshot.targetZone)}, ${target.lowerBpm} to ${target.upperBpm} beats. Good.`;
    }
  }

  const target = zones.find((x) => x.id === snapshot.targetZone);
  if (!target) return "";

  const zoneIdx = (id: HrZoneId) => zones.findIndex((x) => x.id === id);
  if (zoneIdx(currentZone) > zoneIdx(snapshot.targetZone)) {
    return `Heart rate is ${Math.round(snapshot.currentBpm)} in ${zoneLabel(snapshot.zones, currentZone)} — too high for this block. Ease back toward ${zoneLabel(snapshot.zones, snapshot.targetZone)}, ${target.lowerBpm} to ${target.upperBpm} beats.`;
  }
  return `Heart rate is ${Math.round(snapshot.currentBpm)} in ${zoneLabel(snapshot.zones, currentZone)} — below target for this block. Lift effort toward ${zoneLabel(snapshot.zones, snapshot.targetZone)}, ${target.lowerBpm} to ${target.upperBpm} beats.`;
}

/** Deterministic live-coach guidance from measured pace + HR vs plan targets. */
export function buildLocalRunningGuidance(snapshot: RunningCoachSnapshot): string | null {
  if (snapshot.elapsedSeconds < 20) return null;

  const parts: string[] = [];

  if (snapshot.activePhaseSummary) {
    parts.push(snapshot.activePhaseSummary);
  } else if (snapshot.activePhaseLabel) {
    parts.push(`You are in ${snapshot.activePhaseLabel}.`);
  }

  const targetPace =
    snapshot.targetPaceSecondsPerKm && snapshot.targetPaceSecondsPerKm > 0
      ? snapshot.targetPaceSecondsPerKm
      : parseRunningPlan(snapshot.plan).targetPaceSecondsPerKm;

  const curPace = snapshot.currentPaceSecondsPerKm;
  if (targetPace && curPace > 0 && Number.isFinite(curPace)) {
    const pace = comparePaceToTarget(curPace, targetPace);
    if (pace.message) parts.push(pace.message);
  } else if (curPace > 0 && Number.isFinite(curPace)) {
    parts.push(`Current pace about ${formatPaceSpeech(curPace)}.`);
  }

  const hrMsg = buildHrFeedback(snapshot);
  if (hrMsg) parts.push(hrMsg);

  if (parts.length === 0) {
    if (snapshot.frequency === "when_needed") return null;
    const kmWhole = Math.floor(snapshot.distanceKm);
    if (kmWhole >= 1) {
      return `${kmWhole} kilometer${kmWhole !== 1 ? "s" : ""} done — rhythm looks steady. Keep it controlled.`;
    }
    if (snapshot.distanceKm >= 0.3) {
      return `${snapshot.distanceKm.toFixed(1)} km in — stay relaxed through the shoulders and find your rhythm.`;
    }
    const mins = Math.floor(snapshot.elapsedSeconds / 60);
    if (mins >= 1) {
      return `${mins} minute${mins !== 1 ? "s" : ""} in — settle into your breathing and stay smooth.`;
    }
    return "You're off — ease in and find a comfortable rhythm.";
  }

  const joined = parts.join(" ");
  if (snapshot.remainingPhaseSeconds != null && snapshot.remainingPhaseSeconds <= 45 && snapshot.remainingPhaseSeconds > 0) {
    return `${joined} Almost through this block — stay focused, you're doing great.`;
  }
  if (snapshot.paceVerdict === "too_fast") {
    return `${joined} Ease off just a touch — save something for what's next.`;
  }
  if (snapshot.paceVerdict === "on_target" && snapshot.targetZone) {
    return `${joined} That's the spot — lock it in and keep flowing.`;
  }
  return joined;
}

export async function requestLiveRunningGuidance(
  snapshot: RunningCoachSnapshot,
  fallback: string | null,
): Promise<string | null> {
  if (!fallback) return null;

  const timeout = new Promise<{ guidance?: string }>((resolve) => {
    window.setTimeout(() => resolve({ guidance: fallback ?? undefined }), 3500);
  });

  const request = supabase.functions
    .invoke("running-live-coach", {
      body: {
        ...snapshot,
        fallback,
      },
    })
    .then(({ data, error }) => {
      if (error) return { guidance: baseline };
      return data as { guidance?: string };
    })
    .catch(() => ({ guidance: baseline }));

  const result = await Promise.race([request, timeout]);
  const guidance = result.guidance?.trim();
  return guidance || baseline;
}
